import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import ActionButtons from "./ActionButtons";
import { loadAllLocations } from "../../services/locationService";
import firestoreService from "../../services/firestoreService";
import smsService from "../../services/smsService";
import emailService from "../../services/emailService";

// SMS status visual config
const SMS_STATUS_CONFIG = {
  DELIVERED: {
    label: "Delivered",
    color: "#065f46",
    bg: "#d1fae5",
    border: "#6ee7b7",
  },
  SENT: { label: "Sent", color: "#1e40af", bg: "#dbeafe", border: "#93c5fd" },
  ACCEPTED: {
    label: "Accepted",
    color: "#92400e",
    bg: "#fef3c7",
    border: "#fcd34d",
  },
  UNKNOWN: {
    label: "Unknown",
    color: "#6b7280",
    bg: "#f3f4f6",
    border: "#d1d5db",
  },
  NOT_CONFIGURED: {
    label: "SMS N/A",
    color: "#9ca3af",
    bg: "#f9fafb",
    border: "#e5e7eb",
  },
};

const SmsBadge = ({ status }) => {
  const cfg = SMS_STATUS_CONFIG[status] || SMS_STATUS_CONFIG.UNKNOWN;
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "0.75rem",
        fontWeight: "600",
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
};

const RegistrationRow = ({
  registration,
  serialNumber,
  onApprove,
  onReject,
  isLoading,
}) => {
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [userAccount, setUserAccount] = useState(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleMsg, setRoleMsg] = useState("");
  const [smsStatus, setSmsStatus] = useState(null); // live status string
  const [smsLoading, setSmsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  const u = registration.userInfo || {};
  const r = registration.representativeInfo || {};

  // Fetch SMS status once when the message ID is available
  useEffect(() => {
    const msgId = registration.smsMessageId;
    if (!msgId || smsStatus) return;
    setSmsLoading(true);
    smsService
      .getStatus(msgId)
      .then((data) => setSmsStatus(data.status || "UNKNOWN"))
      .catch(() => setSmsStatus("UNKNOWN"))
      .finally(() => setSmsLoading(false));
  }, [registration.smsMessageId]);

  useEffect(() => {
    if (showDetailModal) {
      if (!locationData)
        loadAllLocations()
          .then(setLocationData)
          .catch(() => {});
      if (u.email) {
        firestoreService
          .getUserAccount(u.email)
          .then(setUserAccount)
          .catch(() => {});
      }
    }
  }, [showDetailModal]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const getStatusBadge = (status) => {
    const cls = {
      pending: "status-pending",
      approved: "status-approved",
      rejected: "status-rejected",
    };
    return (
      <span className={`status-badge ${cls[status] || ""}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const resolveCityName = (cityId) => {
    if (!cityId) return null;
    if (!locationData) return cityId;
    return locationData.cityMap?.[cityId]?.name || cityId;
  };

  const handleRoleToggle = async (roleField) => {
    if (!u.email || !userAccount) return;
    const currentValue = userAccount[roleField] === true;
    const newValue = !currentValue;
    setRoleLoading(true);
    setRoleMsg("");
    try {
      await firestoreService.setUserRole(u.email, roleField, newValue);
      setUserAccount((prev) => ({ ...prev, [roleField]: newValue }));
      const roleName = roleField === "isAdmin" ? "Admin" : "Moderator";
      setRoleMsg(
        newValue ? `${roleName} role assigned.` : `${roleName} role revoked.`,
      );
      setTimeout(() => setRoleMsg(""), 3000);
    } catch (e) {
      setRoleMsg("Error: " + e.message);
    } finally {
      setRoleLoading(false);
    }
  };

  /** Resend approval SMS + email for approved customers */
  const handleResendCode = async () => {
    const phone = u.mobileNumber;
    if (!phone) {
      setResendMsg({ type: "error", text: "No phone number on record." });
      return;
    }
    // Need the temporaryPassword from user_accounts
    let tempPwd = userAccount?.temporaryPassword;
    if (!tempPwd && u.email) {
      try {
        const acc = await firestoreService.getUserAccount(u.email);
        tempPwd = acc?.temporaryPassword;
      } catch (_) {}
    }
    if (!tempPwd) {
      setResendMsg({
        type: "error",
        text: "Temporary password not found. Cannot resend.",
      });
      return;
    }

    setResendLoading(true);
    setResendMsg(null);
    try {
      const loginUrl = `${window.location.origin}/admin/login`;

      // Fetch latest account to get current role — user may have been promoted since approval
      let latestAccount = userAccount;
      if (!latestAccount && u.email) {
        try {
          latestAccount = await firestoreService.getUserAccount(u.email);
        } catch (_) {}
      }

      // Merge role flags from user_accounts into userInfo so buildApprovalMessage sees them
      const userInfoWithRoles = {
        ...u,
        isAdmin: latestAccount?.isAdmin === true,
        isModerator: latestAccount?.isModerator === true,
      };

      // Send SMS — get full response to show result to admin
      const smsText = smsService.buildApprovalMessage(
        u.fullName,
        tempPwd,
        loginUrl,
        userInfoWithRoles,
      );
      const smsResult = await smsService.send(phone, smsText);

      if (
        smsResult.sent === false ||
        smsResult.status === "API_ERROR" ||
        smsResult.status === "NETWORK_ERROR" ||
        smsResult.status === "NOT_CONFIGURED"
      ) {
        // SMS failed — show detailed error
        setResendMsg({
          type: "error",
          text: `SMS failed: ${smsResult.error || "Unknown error"}`,
          detail: smsResult.rawBody || null,
        });
      } else {
        // SMS succeeded
        if (smsResult.id) {
          await firestoreService.updateRegistrationSmsId(
            registration.id,
            smsResult.id,
          );
          setSmsStatus("ACCEPTED");
        }

        // Also resend email (non-fatal)
        let emailNote = "";
        try {
          await emailService.sendApprovalEmail(
            u.email,
            tempPwd,
            userInfoWithRoles,
          );
          emailNote = " and email";
        } catch (_) {}

        setResendMsg({
          type: "success",
          text: `✅ Code resent via SMS${emailNote}. Status: ${smsResult.status || "ACCEPTED"}, Segments: ${smsResult.segments ?? 1}`,
        });
        setTimeout(() => setResendMsg(null), 8000);
      }
    } catch (e) {
      setResendMsg({ type: "error", text: `Failed to resend: ${e.message}` });
    } finally {
      setResendLoading(false);
    }
  };

  const detailModal = showDetailModal
    ? createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              background: "var(--bg-primary)",
              borderRadius: "0.75rem",
              width: "100%",
              maxWidth: "560px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                background:
                  "linear-gradient(135deg, var(--color-primary), var(--color-info))",
                padding: "1.25rem 1.5rem",
                borderRadius: "0.75rem 0.75rem 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <div>
                <h3 style={{ color: "#fff", margin: 0, fontSize: "1.125rem" }}>
                  Registration Details
                </h3>
                <p
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    margin: "0.25rem 0 0",
                    fontSize: "0.875rem",
                  }}
                >
                  #{serialNumber} — {u.fullName}
                </p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                type="button"
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  color: "#fff",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  fontSize: "1.125rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "1.25rem 1.5rem", flex: 1 }}>
              {/* Status */}
              <div
                style={{
                  marginBottom: "1.25rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                {getStatusBadge(registration.status)}
                {registration.adminRemark && (
                  <span
                    style={{
                      fontSize: "0.8125rem",
                      color: "var(--text-secondary)",
                    }}
                  >
                    Remark: {registration.adminRemark}
                  </span>
                )}
              </div>

              {/* Personal Info */}
              <InfoSection
                title="👤 Personal Information"
                color="var(--color-primary)"
              >
                <InfoRow label="Full Name" value={u.fullName} />
                <InfoRow label="Email" value={u.email} />
                <InfoRow label="Mobile" value={u.mobileNumber} />
                <InfoRow label="Country" value={u.country} />
                <InfoRow label="City" value={resolveCityName(u.cityId)} />
                <InfoRow label="ID Number" value={u.nationalId} />
                {u.passportNumber && (
                  <InfoRow label="Passport" value={u.passportNumber} />
                )}
                {u.referrerContact && (
                  <InfoRow label="Referrer Contact" value={u.referrerContact} />
                )}
              </InfoSection>

              {/* Representative Info */}
              {(r.fullName || r.nationalId || r.cityId || r.kebele) && (
                <InfoSection
                  title="🤝 Representative Information"
                  color="var(--color-success)"
                >
                  <InfoRow label="Full Name" value={r.fullName} />
                  <InfoRow label="ID Number" value={r.nationalId} />
                  <InfoRow label="City" value={resolveCityName(r.cityId)} />
                  <InfoRow label="Kebele" value={r.kebele} />
                </InfoSection>
              )}

              {/* Referrer Info */}
              {registration.referrerInfo && (
                <InfoSection
                  title="🔗 Referred By"
                  color="var(--color-warning)"
                >
                  <InfoRow
                    label="Name"
                    value={registration.referrerInfo.referrerName}
                  />
                  <InfoRow
                    label="Email"
                    value={registration.referrerInfo.referrerEmail}
                  />
                  <InfoRow
                    label="Phone"
                    value={registration.referrerInfo.referrerPhone}
                  />
                </InfoSection>
              )}

              {/* Timestamps */}
              <InfoSection title="📅 Timestamps" color="var(--color-info)">
                <InfoRow
                  label="Submitted"
                  value={formatDate(registration.createdAt)}
                />
                <InfoRow
                  label="Updated"
                  value={formatDate(registration.updatedAt)}
                />
              </InfoSection>

              {/* Role Management — only show if user has an account (approved) */}
              {registration.status === "approved" && (
                <InfoSection
                  title="🔐 Role Management"
                  color="var(--color-danger, #e53e3e)"
                >
                  {!userAccount ? (
                    <p
                      style={{
                        fontSize: "0.875rem",
                        color: "var(--text-secondary)",
                      }}
                    >
                      No user account found for this registration.
                    </p>
                  ) : (
                    <>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.75rem",
                          flexWrap: "wrap",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleRoleToggle("isAdmin")}
                          disabled={roleLoading}
                          style={{
                            padding: "0.5rem 1rem",
                            borderRadius: "0.375rem",
                            border: "none",
                            cursor: "pointer",
                            fontWeight: "500",
                            fontSize: "0.875rem",
                            background: userAccount.isAdmin
                              ? "var(--color-danger-bg, #fff5f5)"
                              : "var(--color-primary)",
                            color: userAccount.isAdmin
                              ? "var(--color-danger, #e53e3e)"
                              : "#fff",
                          }}
                        >
                          {userAccount.isAdmin
                            ? "Revoke Admin"
                            : "Assign Admin"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRoleToggle("isModerator")}
                          disabled={roleLoading}
                          style={{
                            padding: "0.5rem 1rem",
                            borderRadius: "0.375rem",
                            border: "none",
                            cursor: "pointer",
                            fontWeight: "500",
                            fontSize: "0.875rem",
                            background: userAccount.isModerator
                              ? "var(--color-danger-bg, #fff5f5)"
                              : "var(--color-success)",
                            color: userAccount.isModerator
                              ? "var(--color-danger, #e53e3e)"
                              : "#fff",
                          }}
                        >
                          {userAccount.isModerator
                            ? "Revoke Moderator"
                            : "Assign Moderator"}
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Current:{" "}
                        {userAccount.isAdmin
                          ? "🛡️ Admin"
                          : userAccount.isModerator
                            ? "🔧 Moderator"
                            : "👤 Regular User"}
                      </div>
                      {roleMsg && (
                        <div
                          style={{
                            marginTop: "0.5rem",
                            fontSize: "0.875rem",
                            color: roleMsg.startsWith("Error")
                              ? "var(--color-danger)"
                              : "var(--color-success)",
                          }}
                        >
                          {roleMsg}
                        </div>
                      )}
                    </>
                  )}
                </InfoSection>
              )}

              {/* SMS send result banner — shown after resend attempt */}
              {resendMsg && (
                <div
                  style={{
                    marginTop: "0.75rem",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    background:
                      resendMsg.type === "success"
                        ? "var(--color-success-bg, #f0fdf4)"
                        : "var(--color-danger-bg, #fff5f5)",
                    border: `1px solid ${resendMsg.type === "success" ? "var(--color-success, #10b981)" : "var(--color-danger, #ef4444)"}`,
                    fontSize: "0.875rem",
                    color:
                      resendMsg.type === "success"
                        ? "var(--color-success, #065f46)"
                        : "var(--color-danger, #b91c1c)",
                  }}
                >
                  <strong>{resendMsg.text}</strong>
                  {resendMsg.detail && (
                    <div
                      style={{
                        marginTop: "0.25rem",
                        fontSize: "0.75rem",
                        opacity: 0.8,
                        wordBreak: "break-all",
                      }}
                    >
                      Raw response: {resendMsg.detail}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "1rem 1.5rem",
                borderTop: "1px solid var(--border-primary)",
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
                flexShrink: 0,
              }}
            >
              {registration.receiptUrl && (
                <button
                  className="receipt-preview-btn"
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowReceiptModal(true);
                  }}
                  type="button"
                >
                  View Receipt
                </button>
              )}
              {/* Resend Code — enabled ONLY for approved customers (they have a temporaryPassword) */}
              {registration.status === "approved" ? (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendLoading}
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "0.375rem",
                    border: "none",
                    background: resendLoading
                      ? "var(--bg-secondary)"
                      : "var(--color-info, #3b82f6)",
                    color: resendLoading ? "var(--text-secondary)" : "#fff",
                    cursor: resendLoading ? "not-allowed" : "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  {resendLoading ? "Sending..." : "📱 Resend Code"}
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "0.375rem",
                    border: "1px solid var(--border-primary)",
                    background: "var(--bg-secondary)",
                    color: "var(--text-secondary)",
                    cursor: "not-allowed",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    opacity: 0.45,
                  }}
                  title="Only available after customer is approved"
                >
                  📱 Resend Code
                </button>
              )}
              <button
                className="cancel-btn"
                onClick={() => setShowDetailModal(false)}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  const receiptModal = showReceiptModal
    ? createPortal(
        <div
          className="receipt-modal-overlay"
          onClick={() => setShowReceiptModal(false)}
        >
          <div className="receipt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-modal-header">
              <h3>Payment Receipt</h3>
              <button
                className="close-modal-btn"
                onClick={() => setShowReceiptModal(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className="receipt-modal-content">
              <img
                src={registration.receiptUrl}
                alt="Payment Receipt"
                className="receipt-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  const embed = document.createElement("embed");
                  embed.src = registration.receiptUrl;
                  embed.type = "application/pdf";
                  embed.className = "receipt-pdf";
                  e.target.parentNode.appendChild(embed);
                }}
              />
            </div>
            <div className="receipt-modal-footer">
              <a
                href={registration.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="download-receipt-btn"
              >
                Download Receipt
              </a>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <tr className="registration-row">
        <td
          className="registration-cell"
          style={{
            textAlign: "center",
            color: "var(--text-secondary)",
            fontWeight: "500",
          }}
        >
          {serialNumber}
        </td>
        <td className="registration-cell" style={{ textAlign: "left" }}>
          <button
            type="button"
            onClick={() => setShowDetailModal(true)}
            style={{
              background: "none",
              border: "none",
              color: "var(--color-primary)",
              cursor: "pointer",
              fontWeight: "500",
              textDecoration: "underline",
              padding: 0,
              textAlign: "left",
              display: "block",
              lineHeight: "1.4",
            }}
          >
            {u.fullName || "N/A"}
          </button>
        </td>
        <td className="registration-cell">{u.email || "N/A"}</td>
        <td className="registration-cell">{u.mobileNumber || "N/A"}</td>
        <td className="registration-cell">{u.country || "N/A"}</td>
        <td className="registration-cell">
          {getStatusBadge(registration.status)}
        </td>
        <td className="registration-cell">
          {registration.receiptUrl ? (
            <button
              className="receipt-preview-btn"
              onClick={() => setShowReceiptModal(true)}
              type="button"
            >
              View Receipt
            </button>
          ) : (
            <span className="no-receipt">No Receipt</span>
          )}
        </td>
        <td className="registration-cell">
          {formatDate(registration.createdAt)}
        </td>
        {/* SMS Delivery Status */}
        <td className="registration-cell">
          {!registration.smsMessageId ? (
            <span
              style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}
            >
              —
            </span>
          ) : smsLoading ? (
            <span
              style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}
            >
              ...
            </span>
          ) : (
            <SmsBadge status={smsStatus || "ACCEPTED"} />
          )}
        </td>
        <td className="registration-cell actions-cell">
          <ActionButtons
            registration={registration}
            onApprove={onApprove}
            onReject={onReject}
            isLoading={isLoading}
          />
        </td>
      </tr>
      {detailModal}
      {receiptModal}
    </>
  );
};

const InfoSection = ({ title, color, children }) => (
  <div
    style={{
      marginBottom: "1rem",
      borderRadius: "0.5rem",
      border: "1px solid var(--border-primary)",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        background: color,
        padding: "0.5rem 0.875rem",
        fontSize: "0.875rem",
        fontWeight: "600",
        color: "#fff",
      }}
    >
      {title}
    </div>
    <div
      style={{ padding: "0.75rem 0.875rem", background: "var(--bg-secondary)" }}
    >
      {children}
    </div>
  </div>
);

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div
      style={{
        display: "flex",
        padding: "0.3rem 0",
        borderBottom: "1px solid var(--border-primary)",
        fontSize: "0.875rem",
      }}
    >
      <span
        style={{
          color: "var(--text-secondary)",
          width: "140px",
          flexShrink: 0,
          fontWeight: "500",
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: "var(--text-primary)",
          flex: 1,
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
};

export default RegistrationRow;
